import { format } from 'date-fns'

export const SECURITY_DEPOSIT_REASON_REQUIRED_MESSAGE =
  'Reason for Change is required when updating Unused Security Deposit.'

export const SECURITY_DEPOSIT_REASON_OPTIONS = ['Cleaning', 'Damage', 'Legal'] as const

const SECURITY_DEPOSIT_REASON_ALIASES: Record<string, string> = {
  'Legal Charges': 'Legal',
}

export function normalizeSecurityDepositChangeReason(reason: string): string {
  const trimmed = reason.trim()
  return SECURITY_DEPOSIT_REASON_ALIASES[trimmed] ?? trimmed
}

export function securityDepositMoneyValue(value: unknown): number {
  const num = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(num) ? Number(num.toFixed(2)) : 0
}

export function formatSecurityDepositReasonsForForm(value: unknown): string {
  const raw = Array.isArray(value)
    ? value.map((item) => String(item))
    : String(value ?? '').split(',')
  const allowed = new Set<string>(SECURITY_DEPOSIT_REASON_OPTIONS)
  const normalized: string[] = []
  for (const item of raw) {
    const reason = normalizeSecurityDepositChangeReason(item)
    if (!reason || !allowed.has(reason) || normalized.includes(reason)) continue
    normalized.push(reason)
  }
  return normalized.join(', ')
}

export function hasSecurityDepositReason(value: unknown): boolean {
  return Boolean(formatSecurityDepositReasonsForForm(value))
}

export function hasPersistedUnusedSecurityDeposit(value: unknown): boolean {
  if (value === undefined || value === null) return false
  return String(value).trim() !== ''
}

export function isUnusedSecurityDepositChanged(
  currentUnused: unknown,
  persistedUnused: unknown,
): boolean {
  return securityDepositMoneyValue(currentUnused) !== securityDepositMoneyValue(persistedUnused)
}

/**
 * Reason is required when Unused Security Deposit changed from the persisted
 * calculation value (or from the property value when no persisted calc value exists).
 */
export function resolveUnusedSecurityDepositBaseline(
  persistedUnusedSecurityDeposit?: unknown,
  propertyUnusedSecurityDeposit?: unknown,
): unknown {
  return hasPersistedUnusedSecurityDeposit(persistedUnusedSecurityDeposit)
    ? persistedUnusedSecurityDeposit
    : propertyUnusedSecurityDeposit
}

export function nextSecurityDepositReasonsAfterUnusedChange(args: {
  nextUnused: unknown
  persistedUnusedSecurityDeposit?: unknown
  propertyUnusedSecurityDeposit?: unknown
  persistedReasons: unknown
}): string {
  const baseline = resolveUnusedSecurityDepositBaseline(
    args.persistedUnusedSecurityDeposit,
    args.propertyUnusedSecurityDeposit,
  )
  if (isUnusedSecurityDepositChanged(args.nextUnused, baseline)) {
    return ''
  }
  return formatSecurityDepositReasonsForForm(args.persistedReasons)
}

export function isSecurityDepositReasonMissing(args: {
  unusedSecurityDeposit: unknown
  persistedUnusedSecurityDeposit?: unknown
  propertyUnusedSecurityDeposit?: unknown
  reasons: unknown
}): boolean {
  const unusedChanged = isUnusedSecurityDepositChanged(
    args.unusedSecurityDeposit,
    resolveUnusedSecurityDepositBaseline(
      args.persistedUnusedSecurityDeposit,
      args.propertyUnusedSecurityDeposit,
    ),
  )
  const unusedDiffersFromProperty = isUnusedSecurityDepositChanged(
    args.unusedSecurityDeposit,
    args.propertyUnusedSecurityDeposit,
  )
  return (unusedChanged || unusedDiffersFromProperty) && !hasSecurityDepositReason(args.reasons)
}

export function formatUnusedSecurityDepositForForm(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === '') return ''
  const num = securityDepositMoneyValue(value)
  return num > 0 ? num.toFixed(2) : ''
}

export function buildPropertyUnusedSecurityDepositResidentDefaultPatch(
  propertyAmount: unknown,
  previousUnused?: unknown,
): Record<string, unknown> {
  const unusedChanged = isUnusedSecurityDepositChanged(propertyAmount, previousUnused)
  return {
    unused_security_deposit: formatUnusedSecurityDepositForForm(propertyAmount),
    security_deposit_change_reasons: null,
    security_deposit_previous_value: unusedChanged
      ? securityDepositMoneyValue(previousUnused).toFixed(2)
      : null,
    security_deposit_updated_by: null,
    security_deposit_updated_by_name: unusedChanged ? 'System' : null,
    security_deposit_updated_at: unusedChanged ? format(new Date(), 'yyyy-MM-dd hh:mm:ss a') : null,
  }
}

type SecurityDepositCalculationForm = {
  resetField: (
    name: 'unused_security_deposit' | 'security_deposit_change_reasons',
    options: { defaultValue: string },
  ) => void
}

/**
 * Mirror the Reimbursement-tab backend side effect locally so Calculation tab
 * shows the copied unused amount and cleared reason/audit without a reload.
 */
export function applyPropertyUnusedSecurityDepositToCalculationFields(
  form: SecurityDepositCalculationForm,
  propertyAmount: unknown,
): void {
  form.resetField('unused_security_deposit', {
    defaultValue: formatUnusedSecurityDepositForForm(propertyAmount),
  })
  form.resetField('security_deposit_change_reasons', {
    defaultValue: '',
  })
}
